"use client";

import { useMemo } from "react";
import { useTheme } from "next-themes";
import type { CandidateProfile } from "@/lib/api/types";
import { transformCandidateProfile } from "@/features/candidate/lib/transform-profile";
import { useProfileHighlights } from "@/features/compliance/hooks/use-profile-highlights";
import { Icon, type IconName } from "@/components/ui/icon";

export function HighlightsPanel({ profile }: { profile: CandidateProfile }) {
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const profileData = useMemo(
    () => transformCandidateProfile(profile),
    [profile],
  );
  const highlights = useProfileHighlights(profileData);

  if (highlights.length === 0) return null;

  

  return (
    <div className="flex flex-col gap-2.5 items-start px-4 sm:px-8 py-4 sm:py-6 w-full border-b border-border">
      <div className="flex items-center py-1.5 w-full">
        <p className="text-lg font-medium text-foreground leading-normal whitespace-nowrap">
          <span>Highlights </span>
          <span className="text-foreground/50">({highlights.length})</span>
        </p>
      </div>
      <div className="flex flex-col gap-2.5 w-full">
        {highlights.map((h) => {
          const rgb = isLight ? (h.lightGradientRgb ?? h.gradientRgb) : h.gradientRgb;
          const iconColor = isLight ? (h.lightColor ?? h.color) : h.color;
          const borderOpacity = isLight ? "0.45" : "0.35";
          // Light mode renders a visible top→bottom color wash (white sheens are
          // invisible on white); dark mode keeps its original sheen + flat fill.
          const backgroundImage = isLight
            ? `linear-gradient(180deg, rgba(${rgb},0.06) 0%, rgba(${rgb},0.2) 100%)`
            : `linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%), linear-gradient(90deg, rgba(${rgb},0.05) 0%, rgba(${rgb},0.05) 100%)`;
          return (
          <div
            key={h.type}
            className="flex flex-col gap-1.5 items-start overflow-hidden p-4 rounded-lg border border-solid opacity-[0.96] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] w-full"
            style={{
              borderColor: `rgba(${rgb},${borderOpacity})`,
              backgroundImage,
            }}
          >
            <div className="flex items-center gap-1.5 w-full min-w-0">
              <div className="shrink-0 size-4">
                <Icon
                  name={h.icon as IconName}
                  size={16}
                  style={{ color: iconColor }}
                />
              </div>
              <p className="text-sm font-medium text-foreground leading-normal truncate min-w-0">
                {h.title}
              </p>
            </div>
            <p className="text-[13px] font-normal text-foreground/70 leading-normal w-full max-sm:line-clamp-2 sm:truncate">
              {h.description}
            </p>
          </div>
          );
        })}
      </div>
    </div>
  );
}
